/* 数字占卦法步骤
准备三组三位数字：
随意选择三组三位数，可以参考日期、电话号码等，确保首位不为零。
计算下卦和上卦：
下卦：将第一组三位数以8除，余数对应下卦（若除尽，余数为8）。
上卦：将第二组三位数以8除，余数对应上卦（同样，若除尽，余数为8）。
计算變爻：
将第三组三位数以6除，余数对应變爻（若除尽，余数为6）。
卦象对应：
余数与先天八卦的数字对应关系如下：
1 = 乾
2 = 兑
3 = 离
4 = 震
5 = 巽
6 = 坎
7 = 艮
0 = 坤

*/

/*在《易經》中，互卦和變卦是從本卦推導而來的重要概念，對於理解卦象的變化和事物的發展具有關鍵作用。

## 本卦、變卦與互卦的定義

1. **本卦**：本卦是占卜時首先得到的卦象，反映當前的情況或事物的初始狀態。

2. **變卦**：變卦是由本卦中某一爻的變化引起的卦象。當本卦中的某個爻（稱為動爻）發生變化時，這個變化會導致新的卦象形成，代表事物未來的發展結果[1][2]。

3. **互卦**：互卦是由本卦的內部爻位重新組合而成的。具體來說，互卦的上卦由本卦中的第三、四、五爻組成，下卦由第二、三、四爻組成。這樣的組合反映了事物發展過程中的內部關係[1][2]。

## 舉例說明

假設我們的本卦是「震為雷」（震卦），其爻象如下：

```
6爻：陽
5爻：陽
4爻：陰
3爻：陽
2爻：陰
1爻：陰
```

### 變卦的推導

如果本卦的第四爻（陽）變為陰，則變卦為「震為風」（風卦），其爻象變為：

```
6爻：陽
5爻：陽
4爻：陰（變爻）
3爻：陽
2爻：陰
1爻：陰
```

這個變卦表達了事物在特定情況下的發展結果。

### 互卦的推導

根據互卦的組合規則，我們可以從本卦中提取爻位來形成互卦。以震卦為例：

- 上卦（互卦）由本卦的第三、四、五爻組成：陽（3爻）、陰（4爻）、陽（5爻）→ 形成的上卦為「震」。
- 下卦（互卦）由本卦的第二、三、四爻組成：陰（2爻）、陽（3爻）、陰（4爻）→ 形成的下卦為「巽」。

因此，互卦為「震巽」，這反映了事物發展過程中的內部動態和交互關係。

## 總結

互卦和變卦的推導是《易經》占卜過程中不可或缺的部分，幫助占卜者理解事物的變化和發展。變卦提供了未來的預測，而互卦則揭示了當前情況下的內部關係，二者共同構成了對卦象的全面分析[1][2][4]。

Citations:
[1] http://www.pingannstudio.url.tw/ch11/4%20IC.pdf
[2] https://blog.csdn.net/cutwind/article/details/78466930
[3] https://www.ximalaya.com/ask/q3405126
[4] https://www.eee-learning.com/blogs/3288
[5] https://www.master-insight.com/%E3%80%8A%E6%98%93%E7%B6%93%EF%BC%8E%E5%9B%B0%E5%8D%A6%E3%80%8B%E5%B0%8D%E9%A6%99%E6%B8%AF%E7%AE%A1%E6%B2%BB%E7%9A%84%E5%95%9F%E7%A4%BA/
[6] https://www.eee-learning.com/article/3041
[7] http://www.pingannstudio.url.tw/ch11/5%20IC.pdf
[8] https://edu.hwadzan.tv/play/52/569/0/301188

*/

import { Injectable } from '@angular/core';
import { iChingData } from './i-ching-data';
import { MatTabGroup } from '@angular/material/tabs';
import { EntityType, idNameItem, IOpenaiCompletionOption, JkBaseComponent, jkLib, JkopenaiService, jkRestReturnClass, login, PageService } from 'jk-service';
import { JcktlService } from '../service/jcktl.service';
//import { AiChatService } from '../../ai/azure-ai.service';
import { Jcktl } from '../_entity_jck';

type Hexagram = {
  number: number;
  name: string;
};

@Injectable({
  providedIn: 'root',
})
export class IChingService extends PageService {
  masterTabGroup!: MatTabGroup;
  /** wizard 步驟：0=基本資料, 1=取卦方式, 2=卦象結果, 3=記錄 */
  currentStep: number = 0;
  /** 是否已完成計算（控制結果頁顯示） */
  calculated: boolean = false;

  n1: number = 0;
  n2: number = 0;
  n3: number = 0;
  lowerTrigramIndex: number = 1; // 下卦
  get lowerTrigramIndexStr(): string {
    return this.lowerTrigramIndex.toString();
  }
  set lowerTrigramIndexStr(value: string) {
    this.lowerTrigramIndex = parseInt(value);
  }
  upperTrigramIndex: number = 1; // 上卦
  get upperTrigramIndexStr(): string {
    return this.upperTrigramIndex.toString();
  }
  set upperTrigramIndexStr(value: string) {
    this.upperTrigramIndex = parseInt(value);
  }
  changingLineIndex: number = 1; // 變爻
  overlappingHexagram: number[] = [1, 1]; // 互卦 (初始給合法值避免越界)
  overlappingName: string = '';
  changeHexagram: number[] = [1, 1]; // 變卦 (初始給合法值避免越界)
  changeName: string = '';
  lowerTrigram: string = '';
  upperTrigram: string = '';
  hexagram: string = ''; // 卦象
  baseDocument: string = ''; // 解卦文
  overlappingDocument: string = ''; // 互卦解卦文
  changeDocument: string = ''; // 變卦
  answer: string = ''; // 占卜結果
  name: string = '路人甲';
  date1: string = jkLib.nowDate()
  age: number = 25;
  question: string = '';
  jcktl005: string = '';
  sex: string = '男';
  // 創建一個8x8的二維數組來表示64卦
  hexagrams: (Hexagram | null)[][] = Array(8)
    .fill(null)
    .map(() => Array(8).fill(null));
  aiResponse: string = '';

  /** 安全取得八卦名稱（1-8 有效，其他回傳空字串） */
  getTrigramName(index: number): string {
    if (index < 1 || index > 8) return '?';
    return this.trigramN[index.toString()] ?? '?';
  }

  /** 安全取得八卦符號（1-8 有效，其他回傳空字串） */
  getTrigramEmoji(index: number): string {
    if (index < 1 || index > 8) return '?';
    return this.emojiMap[index.toString()] ?? '?';
  }
  openaiOpt: IOpenaiCompletionOption = {
    systemPrompt: '',
    n: 1,
    model: 'gpt-4',
    prompt: '',
    max_tokens: 10000 | 100,
    temperature: 0.2,
  }; // = new openaiCompletionOption();
  get modelList(): idNameItem[] {
    if (this.openai)
      return this.openai.modelList
    else
      return [];
  }
  systemPrompt: string = `你是一位易經解卦專業人員.根據問題的本卦 變卦 互卦與變爻與問題來回答問題.
    互卦的上卦由本卦中的第三、四、五爻組成，下卦由第二、三、四爻組成。
    變卦是由本卦的變爻若是陰則變陽,若是陽則變陰,重新產生上下卦稱為變卦。
    需列出變卦,互卦名稱,經文卦意,翼傳注釋,爻辭原文,爻辭說明`;
  hexagramDocument: string = '';
  helpDocument: string = '';
  list64Document: string = '';
  constructor(//private aiChatService: AiChatService,
    public openai: JkopenaiService,
    private jcktlService: JcktlService
  ) {
    super();
    this.initHexagrams();
    this.openai.setdefault(this.openaiOpt);
    this.openaiOpt.model = this.openai.init(this.serverUrl, 8098, this.uid);

    // 將trigramN轉換為idNameItem[]並賦值給trigramMap
    this.trigramMap = Object.keys(this.trigramN).map(key => ({
      id: key,
      name: this.trigramN[key]
    }));
    this.natureList = Object.keys(this.trigramNature).map(key => ({
      id: key,
      name: this.trigramNature[key]
    }));
    this.hexagramDocument = this.printHexagram(false);
    this.helpDocument = this.doHelp();
    this.list64Document = this.printList64();
  }
  // 八卦 乾兌離震巽坎艮坤 1-8 0:陰 1:陽
  trigramBin: string[] = ['空白', '111', '110', '101', '100', '011', '010', '001', '000'];
  trigramN: any = {
    '0': '空白',
    '1': '乾',
    '2': '兑',
    '3': '离',
    '4': '震',
    '5': '巽',
    '6': '坎',
    '7': '艮',
    '8': '坤',
  };
  emojiMap: any = {
    '0': '空白',
    '1': '☰',
    '2': '☱',
    '3': '☲',
    '4': '☳',
    '5': '☴',
    '6': '☵',
    '7': '☶',
    '8': '☷',
  };
  trigramMap: idNameItem[] = []; // 宣告trigramMap為idNameItem[]類型
  natureList: idNameItem[] = []; // 宣告natureList為idNameItem[]類型
  trigramNature: any = { // 自然界象徵
    '0': '空白',
    '1': '天',  // (Heaven)
    '2': '澤', // (Lake)
    '3': '火', // (Fire)
    '4': '雷', // (Thunder)
    '5': '風', // (Wind)
    '6': '水', // (Water)
    '7': '山', // (Mountain)
    '8': '地', // (Earth)
  };
  _documentKind: string = '1';
  get documentKind(): string {
    return this._documentKind;
  };
  set documentKind(value: string) {
    this._documentKind = value;
  }
  documentKindList: idNameItem[] = [{ id: '1', name: '本卦' }, { id: '2', name: '互卦' }, { id: '3', name: '變卦' }];
  _document: string = '';
  get document(): string {
    if (this.documentKind === '1')
      this._document = this.baseDocument;
    else if (this.documentKind === '2')
      this._document = this.overlappingDocument;
    else if (this.documentKind === '3')
      this._document = this.changeDocument;
    return this._document;
  }
  set document(value: string) {
    this._document = value;
  }

  checkParam(): boolean {
    let bool = true;
    if (this.name === '') {
      bool = false;
      alert('姓名不能為空');
    }
    if (this.question === '') {
      bool = false;
      alert('問題不能為空');
    }
    if (this.jcktl005 === '') {
      bool = false;
      alert('分類不能為空');
    }
    return bool;
  }
  calculate() {
    this.lowerTrigramIndex = this.n1 % 8 === 0 ? 8 : this.n1 % 8;
    this.upperTrigramIndex = this.n2 % 8 === 0 ? 8 : this.n2 % 8;
    this.changingLineIndex = this.n3 % 6 === 0 ? 6 : this.n3 % 6;
    //this.loadDocument();
  }
  doHandInput() {
    this.calculate();
    this.doExecute();

  }
  /**  函數：計算互卦的六爻。
   * 互卦是由本卦的內部爻位重新組合而成的。具體來說，互卦的上卦由本卦中的第三、四、五爻組成，下卦由第二、三、四爻組成。這樣的組合反映了事物發展過程中的內部關係[1][2]。
   */
  calculateOverlappingHexagram(low: number, up: number) {
    //let OverlappingHexagram:number[] = [0,0];
    let lowerTrigram: string = this.trigramBin[low];
    let upperTrigram: string = this.trigramBin[up];
    let lowerTrigramArray = lowerTrigram.split('');
    let upperTrigramArray = upperTrigram.split('');
    let all = [];
    all.push(...lowerTrigramArray);
    all.push(...upperTrigramArray);
    let O1: string = all[2 - 1] + all[3 - 1] + all[4 - 1];
    let O2: string = all[3 - 1] + all[4 - 1] + all[5 - 1];
    let Olower: number = this.trigramBin.findIndex((x: string) => x === O1);
    let Oupper: number = this.trigramBin.findIndex((x: string) => x === O2);
    this.overlappingHexagram[0] = Olower;
    this.overlappingHexagram[1] = Oupper;
  }/**
   *  計算變卦的六爻。
   *
   * @memberof IChingService
   */
  calculateChangedHexagram(low: number, up: number) {
    let lowerTrigram: string = this.trigramBin[low];
    let upperTrigram: string = this.trigramBin[up];
    let lowerTrigramArray = lowerTrigram.split('');
    let upperTrigramArray = upperTrigram.split('');
    let all = [];
    all.push(...lowerTrigramArray);
    all.push(...upperTrigramArray);
    if (all[this.changingLineIndex - 1] === '0') {
      all[this.changingLineIndex - 1] = '1';
    } else {
      all[this.changingLineIndex - 1] = '0';
    }
    let O1: string = all[0] + all[1] + all[2];
    let O2: string = all[3] + all[4] + all[5];
    let Olower: number = this.trigramBin.findIndex((x: string) => x === O1);
    let Oupper: number = this.trigramBin.findIndex((x: string) => x === O2);
    this.changeHexagram[0] = Olower;
    this.changeHexagram[1] = Oupper;

  }
  doRandom() {
    if (!this.checkParam()) return;
    this.n1 = this.generateRandomNumber();
    this.n2 = this.generateRandomNumber();
    this.n3 = this.generateRandomNumber();
  }
  doExecute() {
    this.calculate();
    this.loadDocument();
    this.doAnswer();
    this.calculated = true;
    this.currentStep = 2; // 自動跳到結果頁
    this.doAIPrompt();
    if (this.masterTabGroup)
      this.masterTabGroup.selectedIndex = 1;

  }
  getHexagramName(lower: number, upper: number): any {
    let r = { hexagram: '', document: '' };
    // 邊界安全檢查：lower 與 upper 必須在 1-8 範圍內
    if (lower < 1 || lower > 8 || upper < 1 || upper > 8) {
      r.hexagram = '未知卦象';
      r.document = '未知卦象';
      return r;
    }
    let h: any = this.hexagrams[lower - 1][upper - 1];
    let n = h ? h.number : 0;
    if (n === 0) {
      r.hexagram = '未知卦象';
      r.document = '未知卦象';
    } else {
      r.hexagram = h.name;
      r.document = iChingData[n].title + '\n' + iChingData[n].data.join('\n');
    }
    return r;
  }
  loadDocument() {
    this.calculateOverlappingHexagram(this.lowerTrigramIndex, this.upperTrigramIndex);
    this.calculateChangedHexagram(this.lowerTrigramIndex, this.upperTrigramIndex);

    this.lowerTrigram = this.trigramMap.find(item => item.id === this.lowerTrigramIndex.toString())?.name || '';
    this.upperTrigram = this.trigramMap.find(item => item.id === this.upperTrigramIndex.toString())?.name || '';
    let r = this.getHexagramName(this.lowerTrigramIndex, this.upperTrigramIndex);
    /*let h:any =
      this.hexagrams[this.lowerTrigramIndex - 1][this.upperTrigramIndex - 1];
    let n = h ? h.number : 0;
    if (n === 0) {
      this.hexagram = '未知卦象';
      this.document = '未知卦象';
    } else {
      this.hexagram = n+' 卦'+' '+iChingData[n].title;
      this.document = iChingData[n].data.join('\n');
    };
      */
    if (r) {
      this.hexagram = r.hexagram;
      this.baseDocument = r.document;
      this.answer = `上卦：${this.emojiMap[this.upperTrigramIndex]} ${this.upperTrigram}\r
      下卦：${this.emojiMap[this.lowerTrigramIndex]} ${this.lowerTrigram}\r
      變爻：${this.changingLineIndex}\r
      卦象：${this.hexagram}\r `;
    }
    let over = this.getHexagramName(this.overlappingHexagram[0], this.overlappingHexagram[1]);
    this.overlappingName = over.hexagram;
    this.overlappingDocument = over.document;
    let change = this.getHexagramName(this.changeHexagram[0], this.changeHexagram[1]);
    this.changeName = change.hexagram;
    this.changeDocument = change.document;
    if (this.masterTabGroup)
      this.masterTabGroup.selectedIndex = 1;

  }
  doAnswer() {
    this.answer = `本卦 上卦：${this.emojiMap[this.upperTrigramIndex]} ${this.upperTrigram}
    下卦：${this.emojiMap[this.lowerTrigramIndex]} ${this.lowerTrigram}
    變爻：${this.changingLineIndex}
    卦象：${this.hexagram},變卦：${this.changeName},互卦：${this.overlappingName}`;

  }
  async doAIPrompt() {
    let prompt: string = '';
    prompt = '問題:' + this.jcktl005 + ' ' + this.question + '\n' + ' 占卦後得到 ' + this.answer + '\n' +
      '請解釋此卦';
    this.aiResponse = 'ai運算中...';
    this.openaiOpt.systemPrompt = this.systemPrompt;
    this.openaiOpt.prompt = prompt;
    this.aiResponse = await this.openai.sendCompletion(this.openaiOpt);
    this.doPost();
  }
  /*async doAIPromptAzure(){
    let prompt: string = '';
    prompt = '問題:'+this.jcktl005+' '+this.question+'\n'+' 占卦後得到 '+this.answer+'\n'+
    '請解釋此卦';
    this.aiResponse = 'ai運算中...';
    this.aiChatService.getAiResponse(this.systemPrompt,prompt)
    .subscribe((data: any) => {
      this.aiResponse = data.choices[0].message.content;
      this.doPost();
    });
    //this.aiResponse = await this.openai.sendCompletion(this.openaiOpt);
  } */

  generateRandomNumber(): number {
    return Math.floor(Math.random() * (999 - 100 + 1)) + 100;
  }

  list64 = [{ number: 1, name: '乾為天' },
  { number: 2, name: '坤為地' },
  { number: 3, name: '水雷屯' },
  { number: 4, name: '山水蒙' },
  { number: 5, name: '水天需' },
  { number: 6, name: '天水訟' },
  { number: 7, name: '地水師' },
  { number: 8, name: '水地比' },
  { number: 9, name: '風天小畜' },
  { number: 10, name: '天澤履' },
  { number: 11, name: '地天泰' },
  { number: 12, name: '天地否' },
  { number: 13, name: '天火同人' },
  { number: 14, name: '火天大有' },
  { number: 15, name: '地山謙' },
  { number: 16, name: '雷地豫' },
  { number: 17, name: '澤雷隨' },
  { number: 18, name: '山風蠱' },
  { number: 19, name: '地澤臨' },
  { number: 20, name: '風地觀' },
  { number: 21, name: '火雷噬嗑' },
  { number: 22, name: '山火賁' },
  { number: 23, name: '山地剝' },
  { number: 24, name: '地雷復' },
  { number: 25, name: '天雷無妄' },
  { number: 26, name: '山天大畜' },
  { number: 27, name: '山雷頤' },
  { number: 28, name: '澤風大過' },
  { number: 29, name: '坎為水' },
  { number: 30, name: '離為火' },
  { number: 31, name: '澤山咸' },
  { number: 32, name: '雷風恆' },
  { number: 33, name: '天山遯' },
  { number: 34, name: '雷天大壯' },
  { number: 35, name: '火地晉' },
  { number: 36, name: '地火明夷' },
  { number: 37, name: '風火家人' },
  { number: 38, name: '火澤睽' },
  { number: 39, name: '水山蹇' },
  { number: 40, name: '雷水解' },
  { number: 41, name: '山澤損' },
  { number: 42, name: '風雷益' },
  { number: 43, name: '澤天夬' },
  { number: 44, name: '天風姤' },
  { number: 45, name: '澤地萃' },
  { number: 46, name: '地風升' },
  { number: 47, name: '澤水困' },
  { number: 48, name: '水風井' },
  { number: 49, name: '澤火革' },
  { number: 50, name: '火風鼎' },
  { number: 51, name: '震為雷' },
  { number: 52, name: '艮為山' },
  { number: 53, name: '風山漸' },
  { number: 54, name: '雷澤歸妹' },
  { number: 55, name: '雷火豐' },
  { number: 56, name: '火山旅' },
  { number: 57, name: '巽為風' },
  { number: 58, name: '兌為澤' },
  { number: 59, name: '風水渙' },
  { number: 60, name: '水澤節' },
  { number: 61, name: '風澤中孚' },
  { number: 62, name: '雷山小過' },
  { number: 63, name: '水火既濟' },
  { number: 64, name: '火水未濟' }];
  printList64(): string {
    let s = '易經六十四卦列表\n\n';
    this.list64.forEach(item => {
      s += item.number + '\t' + item.name + '\n\n';
    });
    return s;
  }

  initHexagrams() {
    // 填充數組 [下卦][上卦] = 卦象
    this.hexagrams[0][0] = { number: 1, name: '乾為天' };
    this.hexagrams[7][7] = { number: 2, name: '坤為地' };
    this.hexagrams[3][5] = { number: 3, name: '水雷屯' };
    this.hexagrams[5][6] = { number: 4, name: '山水蒙' };
    this.hexagrams[0][5] = { number: 5, name: '水天需' };
    this.hexagrams[5][0] = { number: 6, name: '天水訟' };
    this.hexagrams[5][7] = { number: 7, name: '地水師' };
    this.hexagrams[7][5] = { number: 8, name: '水地比' };
    this.hexagrams[0][4] = { number: 9, name: '風天小畜' };
    this.hexagrams[1][0] = { number: 10, name: '天澤履' };
    this.hexagrams[0][7] = { number: 11, name: '地天泰' };
    this.hexagrams[7][0] = { number: 12, name: '天地否' };
    this.hexagrams[2][0] = { number: 13, name: '天火同人' };
    this.hexagrams[0][2] = { number: 14, name: '火天大有' };
    this.hexagrams[6][7] = { number: 15, name: '地山謙' };
    this.hexagrams[7][3] = { number: 16, name: '雷地豫' };
    this.hexagrams[3][1] = { number: 17, name: '澤雷隨' };
    this.hexagrams[4][6] = { number: 18, name: '山風蠱' };
    this.hexagrams[1][7] = { number: 19, name: '地澤臨' };
    this.hexagrams[7][4] = { number: 20, name: '風地觀' };
    this.hexagrams[3][2] = { number: 21, name: '火雷噬嗑' };
    this.hexagrams[2][6] = { number: 22, name: '山火賁' };
    this.hexagrams[7][6] = { number: 23, name: '山地剝' };
    this.hexagrams[3][7] = { number: 24, name: '地雷復' };
    this.hexagrams[3][0] = { number: 25, name: '天雷無妄' };
    this.hexagrams[0][6] = { number: 26, name: '山天大畜' };
    this.hexagrams[3][6] = { number: 27, name: '山雷頤' };
    this.hexagrams[4][1] = { number: 28, name: '澤風大過' };
    this.hexagrams[5][5] = { number: 29, name: '坎為水' };
    this.hexagrams[2][2] = { number: 30, name: '離為火' };
    this.hexagrams[6][1] = { number: 31, name: '澤山咸' };
    this.hexagrams[4][3] = { number: 32, name: '雷風恆' };
    this.hexagrams[6][0] = { number: 33, name: '天山遯' };
    this.hexagrams[0][3] = { number: 34, name: '雷天大壯' };
    this.hexagrams[7][2] = { number: 35, name: '火地晉' };
    this.hexagrams[2][7] = { number: 36, name: '地火明夷' };
    this.hexagrams[2][4] = { number: 37, name: '風火家人' };
    this.hexagrams[1][2] = { number: 38, name: '火澤睽' };
    this.hexagrams[6][5] = { number: 39, name: '水山蹇' };
    this.hexagrams[5][4] = { number: 40, name: '雷水解' };
    this.hexagrams[1][6] = { number: 41, name: '山澤損' };
    this.hexagrams[3][4] = { number: 42, name: '風雷益' };
    this.hexagrams[0][1] = { number: 43, name: '澤天夬' };
    this.hexagrams[4][0] = { number: 44, name: '天風姤' };
    this.hexagrams[7][1] = { number: 45, name: '澤地萃' };
    this.hexagrams[4][7] = { number: 46, name: '地風升' };
    this.hexagrams[5][1] = { number: 47, name: '澤水困' };
    this.hexagrams[4][5] = { number: 48, name: '水風井' };
    this.hexagrams[2][1] = { number: 49, name: '澤火革' };
    this.hexagrams[4][2] = { number: 50, name: '火風鼎' };
    this.hexagrams[3][3] = { number: 51, name: '震為雷' };
    this.hexagrams[6][6] = { number: 52, name: '艮為山' };
    this.hexagrams[6][4] = { number: 53, name: '風山漸' };
    this.hexagrams[1][3] = { number: 54, name: '雷澤歸妹' };
    this.hexagrams[2][3] = { number: 55, name: '雷火豐' };
    this.hexagrams[6][2] = { number: 56, name: '火山旅' };
    this.hexagrams[4][4] = { number: 57, name: '巽為風' };
    this.hexagrams[1][1] = { number: 58, name: '兌為澤' };
    this.hexagrams[5][4] = { number: 59, name: '風水渙' };
    this.hexagrams[1][5] = { number: 60, name: '水澤節' };
    this.hexagrams[1][4] = { number: 61, name: '風澤中孚' };
    this.hexagrams[6][3] = { number: 62, name: '雷山小過' };
    this.hexagrams[2][5] = { number: 63, name: '水火既濟' };
    this.hexagrams[5][2] = { number: 64, name: '火水未濟' };
  }
  // 打印對照表
  printHexagram(updateDoc: boolean): string {
    //if(this.masterTabGroup)
    //  this.masterTabGroup.selectedIndex = 1;
    let s = '';
    for (let i = 0; i < 8; i++) {
      for (let j = 0; j < 8; j++) {
        const hexagram = this.hexagrams[i][j];
        if (hexagram) {
          s += `下卦 ${this.trigramN[i + 1]} [${i + 1}] 上卦 ${this.trigramN[j + 1]} [${j + 1}] = 第 ${hexagram.number} 卦:(${hexagram.name})\n\n `;
        }
      }
    }
    if (updateDoc)
      this.document = s;
    return s;
  }

  // 定義一個函數來查找特定的卦
  findHexagram(number: number): Hexagram | null {
    for (let i = 0; i < 8; i++) {
      for (let j = 0; j < 8; j++) {
        const hexagram = this.hexagrams[i][j];
        if (hexagram && hexagram.number === number) {
          return hexagram;
        }
      }
    }
    return null;
  }
  doHelp(): string {
    //if(this.masterTabGroup)
    //  this.masterTabGroup.selectedIndex = 2;
    const ask = `提問方法:\n
	1.每次一個問題,一旦決定後後果如何? 例如小孩可選兩個學校,須分兩次占,第一次問:我是OOO,想問我的孩子 <姓名> 去念A學校 不知可否? 第二次問:我是OOO,想問我的孩子 <姓名> 去念B學校 不知可否?\n
	2.適合的問題: 時運,經商,婚姻, 事業, 健康, 子嗣\n
	3.同一問題,可以換不同方式來占,一旦有了結果,則須過三個月(一季)再占\n
	4.最好在清晨,心思清淨,意念集中. 先擬好問題,準備紙筆寫下.拿出籌策(若數字占則不用),心中默念禱詞(若不想麻煩就照自己的話講就好):\n
假爾泰筮有常,某(自己名字)今以某事(想要占問之事),未知可否.原質所疑於神之靈,吉凶,得失,悔吝,憂虞 惟爾有神 尚明告之.\n
	5.遵守三不占原則:不誠不占,不義不占,不疑不占\n
	6.解占者不可忽略 解卦所需 的生活經驗,以及個人主觀動能力量.\n
`;
    //const hex = this.printHexagram(false);
    //this.helpDocument = ask+hex;
    return ask;
  }

  async doPost() {
    let tl: Jcktl = await this.jcktlService.doInsert();
    //tl.TL001 = jkLib.nowDate() + '-' + jkLib.nowTime9();
    tl.TL003 = this.name; //  問卦者                    V     10
    //0004 TL004      時間                      V      1
    tl.TL005 = this.jcktl005; //      分類                      V     20
    tl.TL006 = this.question; //   問題                      T      X
    tl.TL007 = this.aiResponse; //      回答                      T      X
    tl.TL008 = this.upperTrigramIndex; //      上卦                      N      6
    tl.TL009 = this.lowerTrigramIndex; //   下卦                      N      6
    tl.TL010 = this.changingLineIndex; //     變爻                      N      6
    tl.TL011 = this.age; //       年齡                      N      6
    tl.TL012 = this.sex;//       性別                      V      2
    //0002 TL013      DateTime                  D      X
    let ret = new jkRestReturnClass();
    try {
      await this.jcktlService.insertData(tl, false, ret);
    } catch (error) {
      alert(error);
    }
    //this.jcktlService.insertTable(tl,EntityType.data,,ret);
    alert('OK.' + ret.getResult());
  }
  doCopyClipboard() {
    let text = this.answer + '\n' + this.aiResponse;
    jkLib.copy2Clipboard(text);
    alert('已複製到剪貼簿');
  }
  /*async loadHexagramJson(n: number): Promise<string> {
    const fn = `./assets/i-ching/${n}.json`;
    try {
      const response = await fetch(fn);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const rawdata = await response.json();
      return JSON.stringify(rawdata);
    } catch (error) {
      console.error('Error loading hexagram JSON:', error);
      return '';
    }
  }*/
  // 使用示例
  //console.log(findHexagram(1));  // { number: 1, name: "乾為天" }
  //console.log(findHexagram(64)); // { number: 64, name: "火水未濟" }
}
